import { Box, Card, CardBody, ChakraProvider, Container, Divider, Heading, Link, Text, useColorModeValue, VStack } from "@chakra-ui/react";
import React from "react";

const PrivacyPolicyPage = () => {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const brandColor = "#CF642A";

  return (
    <ChakraProvider>
      <Box bg={bgColor} minHeight="100vh" py={8}>
        <Container maxW="5xl">
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <Box textAlign="center" py={6}>
              <Heading size="xl" color={brandColor} mb={4}>
                Privacy Policy for EV Charger for Driver App
              </Heading>
              <Text fontSize="lg" color={textColor}>
                Welcome to the EV Charger for Driver app. Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our app. Please read this policy carefully. If you do not agree with the terms of this Privacy Policy, please do not use our app.
              </Text>
            </Box>

            {/* Main Content */}
            <Card bg={cardBg} shadow="lg">
              <CardBody p={8}>
                <VStack spacing={6} align="stretch">
                  {/* Section 1 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={4}>
                      1. Information We Collect
                    </Heading>

                    <Box mb={4}>
                      <Text fontWeight="bold" color={textColor} mb={2}>
                        Personal Information:
                      </Text>
                      <Text color={textColor} ml={4}>
                        When you register or use the app, we may collect personal data such as your name, email address, phone number, and payment details.
                      </Text>
                    </Box>

                    <Box mb={4}>
                      <Text fontWeight="bold" color={textColor} mb={2}>
                        Location Data:
                      </Text>
                      <Text color={textColor} ml={4}>
                        To help you find nearby EV chargers, we collect your device's location data, either in real-time or periodically.
                      </Text>
                    </Box>

                    <Box mb={4}>
                      <Text fontWeight="bold" color={textColor} mb={2}>
                        Device Information:
                      </Text>
                      <Text color={textColor} ml={4}>
                        We collect information about your device, including device type, operating system, and IP address.
                      </Text>
                    </Box>

                    <Box>
                      <Text fontWeight="bold" color={textColor} mb={2}>
                        Usage Data:
                      </Text>
                      <Text color={textColor} ml={4}>
                        We track how you interact with the app, including the features you use and the time spent on the app.
                      </Text>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Section 2 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      2. How We Use Your Information
                    </Heading>
                    <VStack align="start" spacing={2} ml={4}>
                      <Text color={textColor}>• To provide, maintain, and improve our app and services.</Text>
                      <Text color={textColor}>• To help you locate nearby EV chargers.</Text>
                      <Text color={textColor}>• To communicate with you about updates, offers, or support.</Text>
                      <Text color={textColor}>• To analyze usage trends and improve user experience.</Text>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Section 3 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      3. Sharing Your Information
                    </Heading>
                    <VStack align="start" spacing={2} ml={4}>
                      <Text color={textColor}>• We do not sell your personal information.</Text>
                      <Text color={textColor}>• We may share information with trusted third-party service providers who perform services on our behalf, such as payment processors and cloud services.</Text>
                      <Text color={textColor}>• We may disclose information when required by law or to protect our rights.</Text>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Section 4 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      4. Security of Your Information
                    </Heading>
                    <Text color={textColor} ml={4}>
                      We implement appropriate security measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction.
                    </Text>
                  </Box>

                  <Divider />

                  {/* Section 5 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      5. Your Rights and Choices
                    </Heading>
                    <VStack align="start" spacing={2} ml={4}>
                      <Text color={textColor}>• You can access, update, or delete your personal information through the app settings.</Text>
                      <Text color={textColor}>• You may choose to disable location services, but this may limit app functionality.</Text>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Section 6 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      6. Data Retention
                    </Heading>
                    <Text color={textColor} ml={4}>
                      We retain your information only as long as necessary to provide services and comply with legal obligations.
                    </Text>
                  </Box>

                  <Divider />

                  {/* Section 7 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      7. Children's Privacy
                    </Heading>
                    <Text color={textColor} ml={4}>
                      Our app is not directed at children under the age of 13, and we do not knowingly collect personal information from children.
                    </Text>
                  </Box>

                  <Divider />

                  {/* Section 8 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      8. Changes to This Privacy Policy
                    </Heading>
                    <Text color={textColor} ml={4}>
                      We may update this Privacy Policy periodically. Changes will be effective upon posting on this page.
                    </Text>
                  </Box>

                  <Divider />

                  {/* Section 9 */}
                  <Box>
                    <Heading size="md" color={brandColor} mb={3}>
                      9. Contact Us
                    </Heading>
                    <Text color={textColor} ml={4}>
                      If you have any questions about this Privacy Policy, please contact:
                    </Text>
                    <VStack align="start" ml={8} spacing={1}>
                      <Text color={textColor}>
                        • <strong>Owner:</strong> Cody
                      </Text>
                      <Text color={textColor}>
                        • <strong>Organization:</strong> VET EV Charging Station
                      </Text>
                      <Text color={textColor}>
                        • <strong>Email:</strong>{" "}
                        <Link href="mailto:lakinakim0@gmail.com" color={brandColor}>
                          lakinakim0@gmail.com
                        </Link>
                      </Text>
                      <Text color={textColor}>
                        • <strong>Phone:</strong>{" "}
                        <Link href="tel:+6069307501" color={brandColor}>
                          +6069307501
                        </Link>
                      </Text>
                    </VStack>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </Container>
      </Box>
    </ChakraProvider>
  );
};

export default PrivacyPolicyPage;
